import {
  AlertCategory,
  AlertEvent,
  AlertQueryFieldType,
  type AvailableSubscription,
} from '~/common/models/alert.model';
import { ProductCodes } from '~/common/models/plans.model';
import { BDQueryCriteriaType } from '~/common/models/query.model';

export const USER_ACTIVITY_QUERY = {
  qualifier: BDQueryCriteriaType.IS_EQUAL,
  field: AlertQueryFieldType.CATEGORY,
  values: [AlertCategory.PROFILE] as unknown as object[],
};

export const PROFILE_SUBSCRIPTION_QUERY = {
  qualifier: BDQueryCriteriaType.ARRAY_CONTAINS,
  field: AlertQueryFieldType.REGISTRATIONS,
  values: [{ category: AlertCategory.PROFILE }],
};

export const ACCOUNT_ACTIVITY_QUERY = {
  qualifier: BDQueryCriteriaType.IS_EQUAL,
  field: AlertQueryFieldType.CATEGORY,
  values: [AlertCategory.PROFILE] as unknown as object[],
};

export const VEHICLE_ACTIVITY_QUERY = {
  qualifier: BDQueryCriteriaType.IS_EQUAL,
  field: AlertQueryFieldType.CATEGORY,
  values: [AlertCategory.ASSET] as unknown as object[],
};

export const VEHICLE_DIAGNOSTICS_QUERY = {
  qualifier: BDQueryCriteriaType.IS_EQUAL,
  field: AlertQueryFieldType.CATEGORY,
  values: [AlertCategory.DIAGNOSTIC] as unknown as object[],
};

export enum NotificationSettingsConfigType {
  ADD_VIEW = 'Add View',
}

export const VEHICLE_DIAGNOSTICS_SUBSCRIPTION_QUERY = {
  qualifier: BDQueryCriteriaType.ARRAY_CONTAINS,
  field: AlertQueryFieldType.REGISTRATIONS,
  values: [{ category: AlertCategory.DIAGNOSTIC }],
};

export const VEHICLE_ACTIVITY_SUBSCRIPTION_QUERY = {
  qualifier: BDQueryCriteriaType.ARRAY_CONTAINS,
  field: AlertQueryFieldType.REGISTRATIONS,
  values: [{ category: AlertCategory.ASSET }],
};

export const SAFETY_AND_SECURITY_QUERY = {
  qualifier: BDQueryCriteriaType.IS_EQUAL,
  field: AlertQueryFieldType.CATEGORY,
  values: [AlertCategory.SAFETY] as unknown as object[],
};

export const SAFETY_AND_SECURITY_SUBSCRIPTION_QUERY = {
  qualifier: BDQueryCriteriaType.ARRAY_CONTAINS,
  field: AlertQueryFieldType.REGISTRATIONS,
  values: [{ category: AlertCategory.SAFETY }],
};

/** Keys for the notification groups the settings pages request from the batch subscriptions endpoint. */
export const NOTIFICATION_GROUP_KEYS = {
  ACCOUNT_ACTIVITY: 'accountActivity',
  EMERGENCY: 'emergency',
  DRIVING: 'driving',
  CAMERA: 'camera',
  DIAGNOSTICS: 'diagnostics',
  VEHICLE_ACTIVITY: 'vehicleActivity',
  GEOFENCE: 'geofence',
} as const;

export type NotificationGroupKey =
  (typeof NOTIFICATION_GROUP_KEYS)[keyof typeof NOTIFICATION_GROUP_KEYS];

/** One batch of the batch subscriptions request: a group of events in one category, in display order. */
export type NotificationBatch = {
  key: NotificationGroupKey;
  category: AlertCategory;
  events: readonly AlertEvent[];
};

/** Account notifications page: one Activity notifications group. */
export const ACCOUNT_NOTIFICATION_BATCHES: readonly NotificationBatch[] = [
  {
    key: NOTIFICATION_GROUP_KEYS.ACCOUNT_ACTIVITY,
    category: AlertCategory.PROFILE,
    events: [
      AlertEvent.ACCOUNT_UPDATE,
      AlertEvent.ADD_USER,
      AlertEvent.REMOVE_USER,
    ],
  },
];

/** Emergency notifications on the Safety & Security page, in display order. */
export const EMERGENCY_NOTIFICATION_EVENTS = [
  AlertEvent.AUTOMATIC_CRASH_RESPONSE,
  AlertEvent.THERMAL_EVENT,
  AlertEvent.LOW_LEVEL_COLLISION,
  AlertEvent.THEFT_ALARM,
] as const;

export type EmergencyNotificationEvent =
  (typeof EMERGENCY_NOTIFICATION_EVENTS)[number];

/**
 * Vehicles each emergency notification works for, from the EMER PRD: the
 * first model year, and whether only electric vehicles report the event.
 */
export const EMERGENCY_NOTIFICATION_AVAILABILITY: Record<
  EmergencyNotificationEvent,
  { modelYear: number; isEvOnly: boolean }
> = {
  [AlertEvent.AUTOMATIC_CRASH_RESPONSE]: { modelYear: 2015, isEvOnly: false },
  [AlertEvent.THERMAL_EVENT]: { modelYear: 2022, isEvOnly: true },
  [AlertEvent.LOW_LEVEL_COLLISION]: { modelYear: 2024, isEvOnly: false },
  [AlertEvent.THEFT_ALARM]: { modelYear: 2016, isEvOnly: false },
};

/**
 * Plans a vehicle needs for the vehicle activity and diagnostics notifications:
 * any OnStar plan, which is every vehicle the edit dialog let users pick.
 */
export const VEHICLE_NOTIFICATION_PRODUCT_CODES: readonly ProductCodes[] = [
  ProductCodes.OSS,
  ProductCodes.OST,
  ProductCodes.OSCP,
  ProductCodes.OSOF,
];

/** Route key of the default diagnostics notification, which may have no subscription yet. */
export const DEFAULT_DIAGNOSTICS_KEY = 'default';

/** Plans a vehicle needs for idling and off-hours notifications. */
export const DRIVING_NOTIFICATION_PRODUCT_CODES: readonly ProductCodes[] = [
  ProductCodes.OST,
  ProductCodes.OSOF,
];

/**
 * Delivery a notification gets when it is turned on without any: the
 * notification feed, grouped. The edit dialog starts new notifications the same way.
 */
export const DEFAULT_NOTIFICATION_DELIVERY: Pick<
  AvailableSubscription,
  'receiptPreferences' | 'grouped'
> = {
  receiptPreferences: ['FEED'],
  grouped: true,
};

export const ACCOUNT_ACTIVITY_ALERT_EVENTS = [AlertEvent.ACCOUNT_UPDATE];
export const CAMERA_AND_SAFETY_ALERT_EVENTS = [
  AlertEvent.SAFETY_EVENT,
  AlertEvent.LOW_TRIP_SCORE,
  AlertEvent.ADMIN_PIN_CHANGED,
  AlertEvent.LENS_OBSTRUCTED,
  AlertEvent.CAMERA_ADDED,
  AlertEvent.VEHICLE_IDLING,
  AlertEvent.VEHICLE_OFF_HOURS,
  AlertEvent.VIDEO_CLIP_REQUEST_STATUS,
];

export const DRIVING_NOTIFICATIONS = [
  AlertEvent.SAFETY_EVENT,
  AlertEvent.LOW_TRIP_SCORE,
];

/**
 * Safety-event subtype slugs that do NOT depend on cameras. When the org has no
 * cameras (the camera settings fetch fails) only these subtypes are kept and any
 * other (camera/video dependent) subtype is hidden. Expressed as an allow-list
 * so new video subtypes added by the backend are hidden by default.
 */
export const CAMERA_INDEPENDENT_SAFETY_SUBTYPES = [
  'excessive-speeding',
] as const;
export const CAMERA_NOTIFICATIONS = [
  AlertEvent.ADMIN_PIN_CHANGED,
  AlertEvent.LENS_OBSTRUCTED,
  AlertEvent.CAMERA_ADDED,
  AlertEvent.VIDEO_CLIP_REQUEST_STATUS,
];

export type SENSITIVITY_VALUES = 'LOW' | 'MEDIUM' | 'HIGH';

export const USER_ACTIVITY_ALERT_EVENTS = [
  AlertEvent.ADD_USER,
  AlertEvent.REMOVE_USER,
];

export const VALID_PIN_LENGTH = 4;
export const LAST_DIGIT_INPUT_INDEX = 3;
export const EMPTY_DIGIT_INPUT = '';

export const MAX_SPEED_INPUT = 99;
export const MIN_SPEED_INPUT = 0;
export const SPEED_STEP_INCREMENT = 1;
export const INVALID_SPEED_INPUT_KEYS = ['e', 'E', '+', '-', '.'];
export const MIN_IDLING_THRESHOLD_MINUTES = 1;
export const MAX_IDLING_THRESHOLD_MINUTES = 999;

export const GEOFENCE_LOCATION_BLOCK_NOISE_THRESHOLD = 0;
export const LOW_TRIP_SCORE_THRESHOLD = 60;
export const SAFETY_EVENTS_NOISE_THRESHOLD_MINUTES = 0;
export const VEHICLE_OFF_HOURS_INACTIVITY_IDLING_NOISE_THRESHOLD = 0;
export const VIDEO_CLIP_REQUEST_STATUS_NOISE_THRESHOLD = 0;

export interface TimeWithTimezone {
  time: string;
  timezone: string;
}

export const VEHICLE_OFF_HOURS_DEFAULT_DAYS = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
];
export const VEHICLE_OFF_HOURS_DEFAULT_START_TIME = '08:00';
export const VEHICLE_OFF_HOURS_DEFAULT_END_TIME = '17:00';
export const VEHICLE_INACTIVITY_DEFAULT_DURATION = 10;
export const VEHICLE_IDLING_DEFAULT_DURATION = 10;
